//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import moment from "moment-timezone";
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// event-scheduler imports
import { IContainer, IProps, IState, RoomOptionEnum } from "./EventScheduler.d";
import { eventSchedulerFragments, eventSchedulerQuery } from "./EventScheduler.gql";
import * as s from "./EventScheduler.styles";
// other imports
import { AutopilotFormSidebarScheduler } from "@components/autopilot-form";
import { EventSchedulerAttendeeInput } from "@components/event-scheduler-attendee-input";
import { EventSchedulerTimeInput } from "@components/event-scheduler-time-input";
import { EventSchedulerRoomInput } from "@components/event-scheduler-room-input";
import { EventSchedulerDescriptionInput } from "@components/event-scheduler-description-input";
import { EventSchedulerLocationInput } from "@components/event-scheduler-location-input";
import { EventSchedulerRecurrenceOptionInput } from "@components/event-scheduler-recurrence-option-input";
import { EventSchedulerVideoConferencingInput } from "@components/event-scheduler-video-conferencing-input";
// state
import {
  makeEventSchedulerViewActive,
  updateFeedSchedulerInput,
  updateEventSchedulerHasUnsavedChanges,
  updateScheduledEventIds,
} from "@actions/feed-scheduler.actions";
import { initialFeedSchedulerState } from "@reducers/feed-scheduler.reducer";
import { setActiveViewDay, setActiveViewFeed } from "@actions/chrome-extension.actions";
import { setSelectedMoment, setSnapToSelectedMomentOnNextMount } from "@actions/day-view.actions";
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
// mutation
import { scheduleNewEvent, addAndRemoveOrgTags } from "@mutations";
// util
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { getDomainFromCalendarId } from "@util/calendar-id.util";
import { getRoomsFromAttendees } from "@util/event.util";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { EFlex, EAutopilotWarning, generateEventFormSaveTags } from "@util/event-tag.util";
import { IAutopilotSuggestionOptions } from "@clockwise/client-commons/src/util/event-tag";
import { getCurrentOrg } from "@util/org.util";
import { PostMessageManager } from "@util/post-message.util";
import { EScheduleRange, getMaxMinSlotForRange } from "@util/scheduler.util";
import { isBestTime, isSoonTime } from "@util/scheduling-slot.util";
import { getRenderTimeZone, ZonedMoment } from "@util/time-zone.util";
// material-ui
import { Button, Divider, FormControl, FormHelperText, TextField } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { isOnRecurringEventScheduling } from "@util/experiment.util";

// thoughts:
// This will be a wrapper component that will maintain all the event info state
// each of the subcomponents will work as dumb input components where
// they pass back the values that are selected on an onChange component
// also this component will have to do some validation checks on change
// because changing one attribute of an event could invalidate other
// things(eg. changing duration could affect room availability)

//////////////////
// COMPONENT
//////////////////
class EventSchedulerCmpt extends React.Component<IProps, IState> {
  private textFieldEventTitle: HTMLDivElement | null;
  private eventSchedulerContainer: HTMLElement | null;

  constructor(props: IProps) {
    super(props);
    this.state = this.generateState(props);
    // reset scheduler redux variables
    props.updateFeedSchedulerInput(
      initialFeedSchedulerState.calendarIds,
      initialFeedSchedulerState.roomRequired,
      initialFeedSchedulerState.useAutoSchedule,
      initialFeedSchedulerState.minutesDuration,
      initialFeedSchedulerState.range,
      initialFeedSchedulerState.specifiedDate,
    );

    this.props.relay.refetch(
      {
        calendarIds: this.state.calendarIds,
      },
      undefined,
      () => {
        const org = getCurrentOrg(this.props.viewer);

        // prefill title if there are two attendees
        if (
          org &&
          org.personsByCalendarIdsErrorable.__typename === "PersonList" &&
          org.personsByCalendarIdsErrorable.list.length === 2
        ) {
          const { list } = org.personsByCalendarIdsErrorable;
          const personAName = list[0].profile ? list[0].profile.givenName : null;
          const personBName = list[1].profile ? list[1].profile.givenName : null;

          if (personAName && personBName) {
            this.setState({ title: `${personAName} // ${personBName}` });
          }
        }
      },
    );
  }

  private generateState = (props: IProps) => {
    // set intial event options from feed-scheduler
    // TODO: (maybe better to have chrome-wrapper connected to redux and just pass these in through props for modular reasons)
    const org = getCurrentOrg(props.viewer);
    const userCalId = org && org.primaryOrgEmail;

    let calendarIds =
      userCalId && props.calendarIds.indexOf(userCalId) === -1
        ? [userCalId, ...props.calendarIds]
        : props.calendarIds;
    let includeRoom = props.roomRequired ? RoomOptionEnum.WithRoom : RoomOptionEnum.WithoutRoom;
    let minutesDuration = props.minutesDuration;
    let range = props.range || EScheduleRange.NextSeven;
    let specifiedDate = props.specifiedDate || null;

    // if we have a rescheduler event, set state from it
    if (props.reschedulerEvent) {
      const ev = props.reschedulerEvent;
      const evDate = new Date(props.reschedulerEvent.startTime.dateOrDateTime);
      calendarIds = ev.calendarIds;
      includeRoom = RoomOptionEnum.WithoutRoom;
      minutesDuration = Math.round((ev.endTime.millis - ev.startTime.millis) / 60000);
      if (evDate > new Date()) {
        range = EScheduleRange.AroundDate;
        specifiedDate = evDate;
      }

      const rooms = getRoomsFromAttendees(ev.attendees);
      if (rooms.length) {
        includeRoom = RoomOptionEnum.SpecificRoom;
      }
    }

    const flex =
      range === EScheduleRange.ThisWeek ||
      range === EScheduleRange.NextSeven ||
      range === EScheduleRange.NextWeek
        ? EFlex.Week
        : EFlex.Day;

    const maybeZoomAccount = this.getZoomAccountFromOrg(props);
    const maybeZoomUserLevelAccount = this.getZoomUserLevelAccountFromOrg(props);
    const canUseZoom =
      maybeZoomUserLevelAccount || (maybeZoomAccount && maybeZoomAccount.hasValidZoomAccount);

    return {
      calendarIds,
      includeRoom,
      minutesDuration,
      range,
      specifiedDate,
      title: "",
      roomIds: [],
      description: "",
      location: "",
      includeZoomLink: !!canUseZoom,
      selectedSlot: undefined,
      savingEvent: false,
      openingInGCal: false,
      focusEventTitle: false,
      titleError: "",
      timeError: "",
      uncrawledCalendarsCount: 0,
      openRoomInput: false,
      useAutoSchedule: props.useAutoSchedule,
      flex: !this.maybeGetWarning(props.viewer, calendarIds) ? flex : EFlex.Pin,
      allowSimilarRooms: false,
      conflictResolutionDefragId: undefined,
      allowedStart: null,
      allowedEnd: null,
    };
  };

  public componentDidMount() {
    if (this.state.calendarIds.length > 1) {
      this.props.updateEventSchedulerHasUnsavedChanges(true);
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getZoomAccountFromOrg = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    return (
      (org && org.zoomAccount && org.zoomAccount.__typename === "ZoomAccount" && org.zoomAccount) ||
      undefined
    );
  };

  private getZoomUserLevelAccountFromOrg = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    return (
      (org &&
        org.zoomUserLevelAccount &&
        org.zoomUserLevelAccount.__typename === "ZoomAccount" &&
        org.zoomUserLevelAccount) ||
      undefined
    );
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private maybeFocusTextFieldEventTitle = () => {
    const { title } = this.state;
    if (this.textFieldEventTitle && !title) {
      this.textFieldEventTitle.focus();
    }
  };

  private validateAndMaybeSetState = (state: IState) => {
    // maybe do some validation here
    this.setState({ ...state }, () => {
      setTimeout(() => this.setState({ openRoomInput: false }), 300); // reset this state otherwise room-input will not be able to open again
    });
    this.props.updateEventSchedulerHasUnsavedChanges(true); // make sure i set to false any place i need to
  };

  private onAttendeeChange = (calendarIds: string[]) => {
    // check if any new calendars are bad for autopilot
    const flexWarning = this.maybeGetWarning(this.props.viewer, calendarIds);
    const org = getCurrentOrg(this.props.viewer);
    const newState = Object.assign(
      {},
      this.state,
      { calendarIds, focusEventTitle: true },
      flexWarning ? { flex: flexWarning } : {},
    );
    this.validateAndMaybeSetState(newState);
    // refetch persons
    this.props.relay.refetch({ calendarIds: [org?.primaryOrgCalendar, ...calendarIds] });
  };

  private onTimeChange = (
    minutesDuration: number,
    includeRoom: RoomOptionEnum,
    range: EScheduleRange,
    specifiedDate: Date | null,
    uncrawledCalendarsCount: number,
    selectedSlot?: ISchema.ISchedulingSlot,
    specificRoomClicked?: boolean,
    conflictResolutionDefragId?: string,
  ) => {
    const newState = Object.assign(
      {},
      this.state,
      {
        minutesDuration,
        includeRoom,
        range,
        specifiedDate,
        uncrawledCalendarsCount,
        selectedSlot,
        conflictResolutionDefragId,
        timeError: selectedSlot ? "" : this.state.timeError,
      },
      includeRoom === RoomOptionEnum.WithoutRoom
        ? { roomIds: [] } // if includeRoom is now WithoutRoom or SpecificRoom, reset roomIds
        : {},
      { openRoomInput: specificRoomClicked },
    );
    this.validateAndMaybeSetState(newState);
    this.maybeFocusTextFieldEventTitle();
  };

  private onRoomChange = (roomIds: string[]) => {
    const newState = Object.assign({}, this.state, { roomIds });
    this.validateAndMaybeSetState(newState);
  };

  private onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const title = event.target.value;
    const newState = Object.assign({}, this.state, { title, titleError: "" });
    this.validateAndMaybeSetState(newState);
  };

  private onDescriptionChange = (description: string) => {
    const newState = Object.assign({}, this.state, { description });
    this.validateAndMaybeSetState(newState);
  };

  private onIncludeZoomLinkChange = (includeZoomLink: boolean) => {
    const newState = Object.assign({}, this.state, { includeZoomLink });
    this.validateAndMaybeSetState(newState);
  };

  private onLocationChange = (location: string) => {
    const newState = Object.assign({}, this.state, { location });
    this.validateAndMaybeSetState(newState);
  };

  private onAutopilotChange = ({
    flex,
    allowSimilarRooms,
    timeRange,
  }: IAutopilotSuggestionOptions) => {
    this.setState({
      flex,
      allowSimilarRooms: !!allowSimilarRooms,
      allowedStart: timeRange?.allowedStart ?? null,
      allowedEnd: timeRange?.allowedEnd ?? null,
    });

    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TOGGLE_AUTOPILOT_SETTING, {
      flex,
      allowSimilarRooms,
      schedulingSessionId: this.props.schedulingSessionId,
      ...timeRange,
    });
  };

  private onRecurrenceOptionChange = (recurrenceOption: ISchema.RecurrenceOption) => {
    const newState = Object.assign({}, this.state, { recurrenceOption });
    this.validateAndMaybeSetState(newState);
  };

  private maybeGetWarning = (viewer: ISchema.IViewer, calendarIds: string[]) => {
    const org = getCurrentOrg(viewer);
    if (!org) {
      return undefined;
    }

    const isExternal = calendarIds.some(
      (calendarId) => org.domains.indexOf(getDomainFromCalendarId(calendarId)) === -1,
    );
    if (isExternal) {
      return EAutopilotWarning.ExternalAttendee;
    }

    return undefined;
  };

  private setAutopilotStatus = (eventParent: ISchema.IEventParent) => {
    const { flex, allowSimilarRooms, allowedStart, allowedEnd, calendarIds } = this.state;
    const org = getCurrentOrg(this.props.viewer);
    if (!org) {
      return logger.error("Failed to get org to set autopilot status");
    }

    // construct the tags we'll add or remove
    const saveTags = generateEventFormSaveTags(flex, false, false, allowSimilarRooms);

    // add the org id and eventparent id to the input
    const input: ISchema.IAddAndRemoveOrgTagsMutationInput = {
      orgRelayId: org.id,
      eventParentRelayId: eventParent.id,
      calendarId: calendarIds[0], // seems odd but this is correct, any relevant calendarId will work
      moveRangeV3: {
        allowedStartTimeInclusive: allowedStart!,
        allowedEndTimeInclusive: allowedEnd!,
      },
      ...saveTags,
    };

    addAndRemoveOrgTags(
      this.props.relay.environment,
      input,
      () => null,
      (error?: any) => logger.error("An error occurred setting autopilot status", error),
    );
  };

  private onScheduleSuccess = (
    payload?: ISchema.IScheduleNewEventV2MutationPayload,
    goToEdit?: boolean,
    calendarId?: string,
  ) => {
    this.setState({ savingEvent: false, openingInGCal: false });

    const trackingEvent = !goToEdit
      ? TrackingEvents.SCHEDULING.EVENT_SCHEDULER_MEETING_SCHEDULED
      : TrackingEvents.SCHEDULING.EVENT_SCHEDULER_EDIT_IN_GCAL;
    track(trackingEvent, {
      schedulingSessionId: this.props.schedulingSessionId,
      externalEventId: payload && payload.eventParent.externalEventId,
      scheduledOn: new Date().valueOf(),
      isBestTime: this.state.selectedSlot ? isBestTime(this.state.selectedSlot) : false,
      isSoonestTime: this.state.selectedSlot ? isSoonTime(this.state.selectedSlot) : false,
      numOfAttendees: this.state.calendarIds.length,
      hasConferenceRoom: this.state.roomIds.length > 0,
      isBestRoom: this.state.selectedSlot
        ? this.state.roomIds.indexOf(this.state.selectedSlot.summary.availableRooms[0]) !== -1 // assume best if first room is selected
        : false,
      description: this.state.description,
      location: this.state.location,
      uncrawledCalendarsCount: this.state.uncrawledCalendarsCount,
      hasConflictingAttendees: this.state.selectedSlot
        ? this.state.selectedSlot.summary.conflictingAttendees.length > 0
        : false,
      conflictResolutionDefragId: this.state.conflictResolutionDefragId,
    }); // analytics

    if (payload && !this.maybeGetWarning(this.props.viewer, this.state.calendarIds)) {
      this.setAutopilotStatus(payload.eventParent);
    }

    this.props.updateEventSchedulerHasUnsavedChanges(false); // we have saved the changes so dont pop up unsaved changes modal
    if (goToEdit && payload && calendarId) {
      this.props.snackBarOpen("Your event has been scheduled!"); // only show snack bar when going back to edit
      this.props.makeEventSchedulerViewActive(false);
      PostMessageManager.goToEventEditPage(payload.eventParent.externalEventId, calendarId);
    } else if (!goToEdit && payload && calendarId) {
      this.props.updateScheduledEventIds(payload.eventParent.externalEventId, calendarId); // store in redux for feed-scheduler
      this.props.makeEventSchedulerViewActive(false);
      this.props.setActiveViewFeed();
      const event = payload.eventParent && eventFromParent(payload.eventParent);
      if (event && event.startTime && event.startTime.millis) {
        this.props.setSelectedMoment(new ZonedMoment(event.startTime.millis));
        this.props.setSnapToSelectedMomentOnNextMount(true);
      }
    } else {
      logger.error("Couldn't open up the event edit view in GCal");
    }
  };

  private onScheduleFail = (err: any) => {
    logger.error("Failed to schedule event via EventScheduler", err);
    this.props.snackBarOpen("Sorry, something went wrong. Please try again.");
    this.setState({ savingEvent: false, openingInGCal: false });
  };

  private validateForm = () => {
    const { title, selectedSlot } = this.state;

    const errorMap = {
      error: false,
      titleError: "",
      timeError: "",
    };

    if (!title) {
      errorMap.error = true;
      errorMap.titleError = "Meeting title is required";
    }
    if (!selectedSlot) {
      errorMap.error = true;
      errorMap.timeError = "Meeting time is required";
    }

    return errorMap;
  };

  private scheduleEvent = (goToEdit?: boolean) => {
    const errorMap = this.validateForm();
    if (errorMap.error) {
      (this.eventSchedulerContainer as HTMLElement).scrollTop = 0;
      return this.setState({
        titleError: errorMap.titleError,
        timeError: errorMap.timeError,
      });
    }

    this.setState({ savingEvent: !goToEdit, openingInGCal: !!goToEdit });
    const org = getCurrentOrg(this.props.viewer);
    if (!org) {
      this.onScheduleFail(new Error("Failed to get org to schedule event"));
      return;
    }

    const {
      title,
      selectedSlot,
      roomIds,
      calendarIds,
      location,
      description,
      includeZoomLink,
      conflictResolutionDefragId,
      recurrenceOption,
    } = this.state;
    const timeZone = getRenderTimeZone();
    const startTimeRange = selectedSlot && selectedSlot.timeRange;

    if (!startTimeRange) {
      this.onScheduleFail(new Error("Missing start timeRange"));
      return;
    }

    const attendees = [...calendarIds, ...roomIds];
    const input: ISchema.IScheduleNewEventV2MutationInput = {
      attendees,
      timeZone,
      timeRange: startTimeRange,
      location,
      description,
      title,
      conflictResolutionDefragId,
      includeZoomLink,
      orgRelayId: org.id,
      calendarId: org.primaryOrgEmail,
      recurrenceOption: recurrenceOption ? recurrenceOption : ISchema.RecurrenceOption.None,
    };

    scheduleNewEvent(
      this.props.relay.environment,
      input,
      (payload?: ISchema.IScheduleNewEventV2MutationPayload) =>
        this.onScheduleSuccess(payload, goToEdit, org.primaryOrgEmail),
      this.onScheduleFail,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderVideoConferencingInput = (org: ISchema.IOrg) => {
    const zoomAccount = this.getZoomAccountFromOrg(this.props);
    const zoomUserLevelAccount = this.getZoomUserLevelAccountFromOrg(this.props);

    if (!zoomAccount && !zoomUserLevelAccount) {
      return "";
    }

    return (
      <React.Fragment>
        <EventSchedulerVideoConferencingInput
          includeZoomLink={this.state.includeZoomLink}
          onChange={this.onIncludeZoomLinkChange}
          zoomAccount={zoomAccount}
          zoomUserLevelAccount={zoomUserLevelAccount}
          primaryOrgCalendar={org.primaryOrgCalendar}
        />
        <Divider style={s.divider} />
      </React.Fragment>
    );
  };

  public render() {
    const org = getCurrentOrg(this.props.viewer);
    if (!org) {
      logger.error("Failed to load event-scheduler");
      return <span />;
    }
    let persons: ISchema.IPerson[] = [];
    if (org.personsByCalendarIdsErrorable.__typename === "PersonList") {
      persons = org.personsByCalendarIdsErrorable.list;
    }

    const specifiedMoment = this.state.specifiedDate ? moment(this.state.specifiedDate) : undefined;
    const { rangeMin, rangeMax } = getMaxMinSlotForRange(this.state.range, specifiedMoment);

    const invitees = this.state.calendarIds.map((calendarId) => ({ calendarId, required: true }));
    // If the query has no attendees, or single attendee of just the current user, declare this as a TaskHold.
    const eventTraits = [];
    if (invitees.filter((invitee) => invitee.calendarId !== org.primaryOrgCalendar).length === 0) {
      eventTraits.push(ISchema.EventTrait.TaskHold);
    }

    const schedulerQueryInput = {
      orgId: org.id,
      environment: this.props.relay.environment,
      invitees,
      minutesDuration: this.state.minutesDuration,
      timeZone: getRenderTimeZone(),
      minSlot: rangeMin,
      maxSlot: rangeMax,
      includeRoom: this.state.includeRoom,
      roomsOverride:
        this.state.includeRoom === RoomOptionEnum.SpecificRoom ? this.state.roomIds : [],
      limit: 10,
      seenSlots: [],
      traits: eventTraits,
    };

    return (
      <div ref={(el) => (this.eventSchedulerContainer = el)} style={s.container}>
        <div style={s.eventTitle}>
          <FormControl fullWidth error={!!this.state.titleError}>
            <TextField
              ref={(nameField) => (this.textFieldEventTitle = nameField)}
              autoFocus={this.state.focusEventTitle}
              value={this.state.title}
              onChange={this.onTitleChange}
              label="Add title"
              fullWidth
              cw-id="add-event-title-input"
            />
            {this.state.titleError ? <FormHelperText>{this.state.titleError}</FormHelperText> : ""}
          </FormControl>
        </div>
        <div>
          <Divider style={s.divider} />
        </div>
        <EventSchedulerAttendeeInput
          orgId={org.id}
          personsByCalendarIdsErrorable={org.personsByCalendarIdsErrorable}
          onChange={this.onAttendeeChange}
          calendarIds={this.state.calendarIds}
        />
        <Divider style={s.divider} />
        <EventSchedulerTimeInput
          {...schedulerQueryInput}
          range={this.state.range}
          specifiedDate={this.state.specifiedDate}
          selectedSlot={this.state.selectedSlot}
          onChange={this.onTimeChange}
          open={!this.state.useAutoSchedule} // this opens on init so people have to pick a time first
          persons={persons}
          timeError={this.state.timeError}
          useAutoSchedule={this.state.useAutoSchedule}
          isLoading={false}
          showCloseButton={true}
        />
        {isOnRecurringEventScheduling(org) ? (
          <EventSchedulerRecurrenceOptionInput
            recurrenceOption={this.state.recurrenceOption}
            onChange={this.onRecurrenceOptionChange}
          />
        ) : (
          ""
        )}
        <Divider style={s.divider} />
        {this.state.includeRoom !== RoomOptionEnum.WithoutRoom ? (
          <EventSchedulerRoomInput
            {...schedulerQueryInput}
            selectedSlot={this.state.selectedSlot}
            roomIds={this.state.roomIds}
            onChange={this.onRoomChange}
            open={this.state.openRoomInput}
            useAutoSchedule={this.state.useAutoSchedule}
          />
        ) : (
          ""
        )}
        {this.state.includeRoom !== RoomOptionEnum.WithoutRoom ? <Divider style={s.divider} /> : ""}
        {this.maybeRenderVideoConferencingInput(org)}
        <div style={{ padding: "15px 0" }}>
          <AutopilotFormSidebarScheduler
            org={org}
            selectedDate={new ZonedMoment(this.state.specifiedDate || undefined).format(
              "YYYY-MM-DD",
            )}
            showRoom={this.state.includeRoom !== RoomOptionEnum.WithoutRoom}
            formState={{
              flex: this.state.flex,
              allowSimilarRooms: !!this.state.allowSimilarRooms,
              timeRange: {
                allowedStart: this.state.allowedStart,
                allowedEnd: this.state.allowedEnd,
              },
            }}
            onChange={this.onAutopilotChange}
            warning={this.maybeGetWarning(this.props.viewer, this.state.calendarIds)}
            disableExpansion
          />
        </div>
        <Divider style={s.divider} />
        <EventSchedulerDescriptionInput
          onChange={this.onDescriptionChange}
          description={this.state.description}
        />
        <Divider style={s.divider} />
        <EventSchedulerLocationInput
          onChange={this.onLocationChange}
          location={this.state.location}
        />
        <Divider style={s.divider} />
        <div style={s.buttonContainer}>
          <Button
            color="primary"
            variant="contained"
            classes={{ root: this.props.classes.primaryBtn }}
            onClick={() => this.scheduleEvent()}
            disabled={this.state.savingEvent}
            fullWidth
            cw-id="schedule-button-event-scheduler"
          >
            {this.state.savingEvent ? "Scheduling" : "Schedule"}
          </Button>
          <Button
            variant="text"
            classes={{ root: this.props.classes.secondaryBtn }}
            onClick={() => this.scheduleEvent(true)}
            disabled={this.state.openingInGCal}
            fullWidth
            cw-id="schedule-edit-button-event-scheduler"
          >
            {!this.state.openingInGCal ? "Schedule & Edit" : "Opening in calendar"}
          </Button>
        </div>
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const EventSchedulerStyled = withStyles(s.styles)(
  EventSchedulerCmpt,
) as React.ComponentClass<IProps>;

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    calendarSelectionStates: state.webExtension.calendarSelectionStates,
    calendarIds: state.feedScheduler.calendarIds,
    roomRequired: state.feedScheduler.roomRequired,
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
    reschedulerEvent: state.feedScheduler.reschedulerEvent,
    useAutoSchedule: state.feedScheduler.useAutoSchedule,
    minutesDuration: state.feedScheduler.minutesDuration,
    range: state.feedScheduler.range,
    specifiedDate: state.feedScheduler.specifiedDate,
  };
}

const EventSchedulerRedux = connect(mapStateToProps, {
  setActiveViewDay,
  setActiveViewFeed,
  setSelectedMoment,
  setSnapToSelectedMomentOnNextMount,
  makeEventSchedulerViewActive,
  updateFeedSchedulerInput,
  updateEventSchedulerHasUnsavedChanges,
  updateScheduledEventIds,
  snackBarOpen,
})(EventSchedulerStyled);

//////////////////
// RELAY
//////////////////
export const EventScheduler = createRefetchContainer<IContainer>(
  EventSchedulerRedux,
  eventSchedulerFragments,
  eventSchedulerQuery,
);
